import React, { useCallback, useMemo } from 'react';

import { Box, Paper, Typography, Table, TableBody, TableCell, TableRow, Chip, Stack, Grid } from '@mui/material';
import DOMPurify from 'dompurify';
import moment from 'moment';
import { useSnackbar } from 'notistack';

import { useSecurityInfo, useSecurityBidAsk, useSecurityCS, useSecurityMarketData } from './queries';
import { OrderBook } from '../../../components/OrderBook';
import { SearchInstrumentField } from '../../../components/SearchInstrumentField';
import { searchApi } from '../../../effector/search';
import { formatNumber, formatPercent } from '../../../utils';
import { parseApiError } from '../../../utils/apiErrors';

type InfoRowProps = {
  label: string;
  value: React.ReactNode;
};

const InfoRow: React.FC<InfoRowProps> = ({ label, value }) => (
  <TableRow hover>
    <TableCell
      sx={{
        width: '45%',
        color: 'text.secondary',
        fontWeight: 500,
        py: 0.75,
        px: 2,
        fontSize: '0.8125rem',
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      {label}
    </TableCell>
    <TableCell sx={{ py: 0.75, px: 2, fontSize: '0.8125rem', borderBottom: '1px solid', borderColor: 'divider' }}>
      {value}
    </TableCell>
  </TableRow>
);

type SectionCardProps = {
  title: string;
  children: React.ReactNode;
};

const SectionCard: React.FC<SectionCardProps> = ({ title, children }) => (
  <Paper elevation={1} sx={{ overflow: 'hidden', height: '100%' }}>
    <Box sx={{ backgroundColor: 'grey.100', px: 2, py: 1 }}>
      <Typography variant="subtitle2" color="text.secondary" fontWeight={600}>
        {title}
      </Typography>
    </Box>
    {children}
  </Paper>
);

export const SecurityInfo: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const {
    data: securityInfoData,
    mutateAsync: getSecurityInfo,
    reset: resetSecurityInfo,
    isPending: isSecurityInfoLoading,
  } = useSecurityInfo();
  const { data: securityBidAskData, mutateAsync: getSecurityBidAsk, reset: resetSecurityBidAsk } = useSecurityBidAsk();
  const { data: securityCSData, mutateAsync: getSecurityCS, reset: resetSecurityCS } = useSecurityCS();
  const {
    data: securityMarketDataData,
    mutateAsync: getSecurityMarketData,
    reset: resetSecurityMarketData,
  } = useSecurityMarketData();

  const handleSearchClick = useCallback(
    async (securityKey: string) => {
      const results = await Promise.allSettled([
        getSecurityInfo(securityKey),
        getSecurityBidAsk(securityKey),
        getSecurityCS(securityKey),
        getSecurityMarketData(securityKey),
      ]);

      const rejectedResult = results.find((result): result is PromiseRejectedResult => result.status === 'rejected');

      if (rejectedResult) {
        enqueueSnackbar(parseApiError(rejectedResult.reason, 'Не удалось загрузить данные по ценной бумаге.'), {
          variant: 'error',
        });
      }

      searchApi.clearSuggestion('');
    },
    [enqueueSnackbar, getSecurityInfo, getSecurityBidAsk, getSecurityCS, getSecurityMarketData],
  );

  const handleClearSearch = useCallback(() => {
    resetSecurityInfo();
    resetSecurityBidAsk();
    resetSecurityCS();
    resetSecurityMarketData();
  }, [resetSecurityInfo, resetSecurityBidAsk, resetSecurityCS, resetSecurityMarketData]);

  const securityInfo = useMemo(() => {
    if (!securityInfoData?.result) return null;
    return securityInfoData.result;
  }, [securityInfoData]);

  const securityBidAsk = useMemo(() => {
    if (!securityBidAskData?.result) return null;
    return securityBidAskData.result;
  }, [securityBidAskData]);

  const securityCS = useMemo(() => {
    if (!securityCSData?.result) return null;
    return securityCSData.result;
  }, [securityCSData]);

  const securityMarketData = useMemo(() => {
    if (!securityMarketDataData?.result) return null;
    return securityMarketDataData.result;
  }, [securityMarketDataData]);

  const securityKey = useMemo(() => {
    if (!securityInfo?.ticker || !securityInfo?.classCode) return '';
    return `${securityInfo.ticker} ${securityInfo.classCode} QUIK`;
  }, [securityInfo]);

  const spread = useMemo(() => {
    if (!securityBidAsk?.bid || !securityBidAsk?.ask) return null;
    return Math.abs(securityBidAsk.ask - securityBidAsk.bid).toFixed(2);
  }, [securityBidAsk]);

  const hasData = securityInfo || securityBidAsk;

  return (
    <Stack spacing={2}>
      <SearchInstrumentField onSearch={handleSearchClick} onClear={handleClearSearch} allowFreeSoloSearch />

      {hasData && (
        <Stack spacing={2}>
          {securityInfo && (
            <Paper
              elevation={1}
              sx={{
                px: 3,
                py: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
              }}
            >
              <Stack direction="row" alignItems="baseline" spacing={1.5}>
                <Typography variant="h5" fontWeight={700}>
                  {securityInfo.ticker}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {securityInfo.name}
                </Typography>
                <Chip label={securityInfo.type} size="small" variant="outlined" />
                <Chip label={securityInfo.classCode} size="small" color="default" />
              </Stack>

              <Stack direction="row" alignItems="baseline" spacing={1.5}>
                <Typography variant="h5" fontWeight={700}>
                  {securityInfo.lastPrice}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {securityInfo.currency}
                </Typography>
                <Typography variant="caption" color="text.disabled">
                  {moment(securityInfo.lastTime).format('DD.MM.YYYY HH:mm:ss')}
                </Typography>
              </Stack>
            </Paper>
          )}

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <SectionCard title="Идентификация">
                    <Table size="small">
                      <TableBody>
                        {securityInfo && (
                          <>
                            <InfoRow label="Полное название" value={securityInfo.name} />
                            <InfoRow label="Краткое название" value={securityInfo.shortName} />
                            <InfoRow label="Режим торгов" value={securityInfo.classCode} />
                            <InfoRow label="Площадка" value={securityInfo.className} />
                            <InfoRow
                              label="ISIN"
                              value={
                                <Typography variant="body2" fontFamily="monospace">
                                  {securityInfo.isin}
                                </Typography>
                              }
                            />
                            <InfoRow label="Валюта" value={securityInfo.currency} />
                          </>
                        )}
                      </TableBody>
                    </Table>
                  </SectionCard>
                </Grid>

                <Grid item xs={12} md={6}>
                  <SectionCard title="Торговые параметры">
                    <Table size="small">
                      <TableBody>
                        {securityInfo && (
                          <>
                            <InfoRow label="Размер лота" value={securityInfo.lotSize} />
                            <InfoRow label="Шаг цены" value={securityInfo.priceStep} />
                            <InfoRow
                              label="Шорты"
                              value={
                                <Chip
                                  label={securityInfo.shortsAllowed ? 'Разрешены' : 'Запрещены'}
                                  size="small"
                                  color={securityInfo.shortsAllowed ? 'success' : 'error'}
                                  variant="filled"
                                />
                              }
                            />
                            <InfoRow
                              label="Торгуется сегодня"
                              value={
                                <Chip
                                  label={securityInfo.isCurrent ? 'Да' : 'Нет'}
                                  size="small"
                                  color={securityInfo.isCurrent ? 'success' : 'default'}
                                  variant="filled"
                                />
                              }
                            />
                          </>
                        )}
                      </TableBody>
                    </Table>
                  </SectionCard>
                </Grid>

                <Grid item xs={12} md={6}>
                  <SectionCard title="Аналитика">
                    <Table size="small">
                      <TableBody>
                        {securityInfo && (
                          <>
                            <InfoRow label="Категория" value={securityInfo.category} />
                            <InfoRow label="Ёмкость" value={formatNumber(securityInfo.capacity, 0)} />
                            <InfoRow label="Волатильность" value={formatPercent(securityInfo.volatility, 0)} />
                            <InfoRow
                              label="Стратегии"
                              value={
                                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                  {securityInfo.strategies?.map(({ name }) => (
                                    <Chip key={name} label={name} size="small" color="primary" variant="outlined" />
                                  ))}
                                </Stack>
                              }
                            />
                          </>
                        )}
                      </TableBody>
                    </Table>
                  </SectionCard>
                </Grid>

                <Grid item xs={12} md={6}>
                  <SectionCard title="Котировки">
                    <Table size="small">
                      <TableBody>
                        {securityBidAsk && (
                          <>
                            <InfoRow
                              label="Bid"
                              value={
                                <Typography variant="body2" fontWeight={600} color="success.main">
                                  {securityBidAsk.bid}
                                </Typography>
                              }
                            />
                            <InfoRow
                              label="Ask"
                              value={
                                <Typography variant="body2" fontWeight={600} color="error.main">
                                  {securityBidAsk.ask}
                                </Typography>
                              }
                            />
                            {spread && <InfoRow label="Спред" value={spread} />}
                            <InfoRow label="LastPrice" value={securityBidAsk.lastPrice} />
                            <InfoRow label="FuturesPrice" value={securityBidAsk.futuresPrice || '—'} />
                            <InfoRow
                              label="Статус"
                              value={
                                <Chip
                                  label={securityBidAsk.isOk ? 'OK' : 'Ошибка'}
                                  size="small"
                                  color={securityBidAsk.isOk ? 'success' : 'error'}
                                  variant="filled"
                                />
                              }
                            />
                            {securityBidAsk.errorMessage && (
                              <InfoRow
                                label="Ошибка"
                                value={
                                  <Typography variant="body2" color="error.main">
                                    {securityBidAsk.errorMessage}
                                  </Typography>
                                }
                              />
                            )}
                            <InfoRow
                              label="Обновлено"
                              value={moment(securityBidAsk.timeStamp).format('DD.MM.YYYY HH:mm:ss')}
                            />
                          </>
                        )}
                      </TableBody>
                    </Table>
                  </SectionCard>
                </Grid>

                {securityCS && (
                  <Grid item xs={12} md={6}>
                    <SectionCard title="Балансировщик">
                      <Box
                        sx={{ p: 2, '& pre': { margin: 0, whiteSpace: 'pre-wrap' } }}
                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(securityCS.html) }}
                      />
                    </SectionCard>
                  </Grid>
                )}

                {securityMarketData && (
                  <Grid item xs={12} md={6}>
                    <SectionCard title="Маркетдата">
                      <Box
                        sx={{
                          p: 2,
                          '& table': { width: '100%', borderCollapse: 'collapse' },
                          '& td': {
                            py: 0.5,
                            px: 1,
                            fontSize: '0.8125rem',
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                          },
                        }}
                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(securityMarketData.html) }}
                      />
                    </SectionCard>
                  </Grid>
                )}
              </Grid>
            </Box>

            {securityKey && (
              <Box sx={{ width: 330, flexShrink: 0, position: 'sticky', top: 16 }}>
                <OrderBook securityKey={securityKey} isLoading={isSecurityInfoLoading} />
              </Box>
            )}
          </Box>
        </Stack>
      )}
    </Stack>
  );
};
